import { useState, useEffect } from 'react';
import { Modal, ModalFooter } from './Modal';
import { Input, Textarea, Checkbox } from './FormField';
import { Button } from '../ui/Button';
import { ErrorDisplay } from '../ui/ErrorDisplay';
import { IconPicker } from './IconPicker';
import { tagApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Tag {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  displayAsBadge: boolean;
  displayAsIcon: boolean;
  isSystemTag?: boolean;
}

interface TagModalProps {
  isOpen: boolean;
  onClose: () => void;
  tag?: Tag | null;
  onSuccess: () => void;
}

export function TagModal({
  isOpen,
  onClose,
  tag,
  onSuccess,
}: TagModalProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'sparkles',
    displayAsBadge: true,
    displayAsIcon: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(tag);

  // Populate form when editing
  useEffect(() => {
    if (tag) {
      setFormData({
        name: tag.name || '',
        description: tag.description || '',
        icon: tag.icon || 'sparkles',
        displayAsBadge: tag.displayAsBadge !== undefined ? tag.displayAsBadge : true,
        displayAsIcon: tag.displayAsIcon !== undefined ? tag.displayAsIcon : false,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        icon: 'sparkles',
        displayAsBadge: true,
        displayAsIcon: false,
      });
    }
    setErrors({});
    setSubmitError(null);
  }, [tag, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 50) {
      newErrors.name = 'Name must be 50 characters or less';
    }

    if (formData.description && formData.description.length > 200) {
      newErrors.description = 'Description must be 200 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const data = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        icon: formData.icon || null,
        displayAsBadge: formData.displayAsBadge,
        displayAsIcon: formData.displayAsIcon,
        createdBy: user?.id,
      };

      if (isEditMode && tag) {
        await tagApi.update(tag.id, data);
      } else {
        await tagApi.create(data);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save tag:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to save tag'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!tag) return;

    if (!confirm(`Are you sure you want to delete "${tag.name}"?`)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await tagApi.delete(tag.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to delete tag:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to delete tag'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit tag' : 'Add new tag'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="3rd party vendor"
          required
          error={errors.name}
        />

        <Textarea
          label="Description"
          name="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Initiative involves external vendor integration"
          rows={3}
          error={errors.description}
          helpText="Optional"
        />

        <IconPicker
          label="Icon"
          value={formData.icon}
          onChange={(icon) => setFormData({ ...formData, icon })}
        />

        <div className="space-y-3">
          <label className="text-sm font-medium text-text-primary">
            Display options
          </label>

          <Checkbox
            label="Show in badge list"
            name="displayAsBadge"
            checked={formData.displayAsBadge}
            onChange={(e) =>
              setFormData({ ...formData, displayAsBadge: e.target.checked })
            }
            helpText="Display tag name in the badge list below initiative title"
          />

          <Checkbox
            label="Show as icon on card"
            name="displayAsIcon"
            checked={formData.displayAsIcon}
            onChange={(e) =>
              setFormData({ ...formData, displayAsIcon: e.target.checked })
            }
            helpText="Display tag icon on the initiative card"
          />
        </div>

        <ErrorDisplay error={submitError} />

        <ModalFooter>
          {isEditMode && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="mr-auto"
            >
              Delete
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditMode ? 'Save changes' : 'Add tag'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
